class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');

    this.hoverMQ = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);

    // Hover should persist when moving from the label into the submenu panel.
    this.summary?.addEventListener('mouseenter', this.handleMouseEnter);
    this.summary?.addEventListener('mouseleave', this.handleMouseLeave);
    this.content?.addEventListener('mouseenter', this.handleMouseEnter);
    this.content?.addEventListener('mouseleave', this.handleMouseLeave);
  }

  waitForProductsMegaMedia(grid) {
    if (!grid) return Promise.resolve();

    const imgs = Array.from(grid.querySelectorAll('img')).slice(0, 8);
    const pending = imgs.filter((img) => !img.complete);
    const fontsPromise =
      document.fonts && document.fonts.status !== 'loaded' ? document.fonts.ready.catch(() => {}) : Promise.resolve();

    if (!pending.length) return fontsPromise;

    const decodePromises = pending.map((img) => {
      if (typeof img.decode === 'function') {
        return img.decode().catch(() => {});
      }
      return new Promise((resolve) => img.addEventListener('load', () => resolve(), { once: true }));
    });

    return Promise.race([
      Promise.allSettled([...decodePromises, fontsPromise]),
      new Promise((resolve) => setTimeout(resolve, 250)),
    ]);
  }

  setProductsMegaOffset() {
    if (!this.mainDetailsToggle.hasAttribute('data-products-mega')) return;

    if (!this.mainDetailsToggle.open) {
      this.resetProductsMegaOffset();
      return;
    }

    const summary = this.mainDetailsToggle.querySelector('summary');
    const products = this.mainDetailsToggle.querySelector('.mega-menu__products');
    const grid = this.mainDetailsToggle.querySelector('.mega-menu__products-grid');

    if (!summary || !products || !grid) {
      this.mainDetailsToggle.removeAttribute('data-aligning');
      return;
    }

    products.style.setProperty('--products-mega-x', '0px');

    const viewportCenter = window.innerWidth / 2;
    const gridRect = grid.getBoundingClientRect();
    let offsetPx = Math.round(viewportCenter - (gridRect.left + gridRect.width / 2));

    products.style.setProperty('--products-mega-x', `${offsetPx}px`);

    const correctOffset = (remainingAttempts) => {
      if (!this.mainDetailsToggle.open) {
        this.resetProductsMegaOffset();
        return;
      }

      const currentGridRect = grid.getBoundingClientRect();
      const currentGridCenter = currentGridRect.left + currentGridRect.width / 2;
      const correctionPx = Math.round(viewportCenter - currentGridCenter);

      if (Math.abs(correctionPx) > 1 && remainingAttempts > 0) {
        offsetPx += correctionPx;
        products.style.setProperty('--products-mega-x', `${offsetPx}px`);
        requestAnimationFrame(() => correctOffset(remainingAttempts - 1));
        return;
      }

      this.mainDetailsToggle.removeAttribute('data-aligning');
    };

    requestAnimationFrame(() => correctOffset(2));
  }

  resetProductsMegaOffset() {
    if (!this.mainDetailsToggle.hasAttribute('data-products-mega')) return;
    const products = this.mainDetailsToggle.querySelector('.mega-menu__products');
    if (!products) return;
    products.style.removeProperty('--products-mega-x');
    this.mainDetailsToggle.removeAttribute('data-aligning');
  }

  handleMouseEnter(event) {
    if (!this.hoverMQ.matches) return;

    if (this.hoverCloseTimer) {
      clearTimeout(this.hoverCloseTimer);
      this.hoverCloseTimer = null;
    }

    // If entering the submenu panel, only cancel any pending close.
    if (event?.currentTarget === this.content) return;

    document.querySelectorAll('header-menu details[open]').forEach((openDetails) => {
      if (openDetails === this.mainDetailsToggle) return;
      openDetails.removeAttribute('open');
      openDetails.querySelector('summary')?.setAttribute('aria-expanded', false);
    });

    const isProductsMega = this.mainDetailsToggle.hasAttribute('data-products-mega');
    if (isProductsMega) this.mainDetailsToggle.setAttribute('data-aligning', '');

    this.mainDetailsToggle.open = true;
    this.mainDetailsToggle.querySelector('summary')?.setAttribute('aria-expanded', true);
  }

  handleMouseLeave() {
    if (!this.hoverMQ.matches) return;

    // While aligning, the panel can be temporarily non-interactive.
    if (this.mainDetailsToggle.hasAttribute('data-aligning')) return;

    this.hoverCloseTimer = setTimeout(() => {
      // Don't close if the user is hovering the label or the submenu panel, or focusing within it.
      // (Avoid using `details:hover` because it can include "gaps" that keep the menu open incorrectly.)
      if (this.summary && this.summary.matches(':hover')) return;
      if (this.content && this.content.matches(':hover')) return;
      if (this.contains(document.activeElement)) return;
      this.close();
    }, 180);
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (this.mainDetailsToggle.open) {
      if (this.mainDetailsToggle.hasAttribute('data-products-mega')) {
        this.mainDetailsToggle.setAttribute('data-aligning', '');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const grid = this.mainDetailsToggle.querySelector('.mega-menu__products-grid');
            this.waitForProductsMegaMedia(grid).then(() => this.setProductsMegaOffset());
          });
        });
      }
    } else {
      this.resetProductsMegaOffset();
    }

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`,
    );
  }
}

customElements.define('header-menu', HeaderMenu);
